/**
 * The artifact table view.
 *
 * The units are canonical; the layout only *references* them by id. That separation is
 * what makes a cell's citations belong to the cell rather than to a row, and it is why
 * this view renders from `units` and uses `layout` purely for ordering.
 *
 * Four cell states are visually distinct, and the distinction is the point:
 *
 * - **grounded** — cited; the markers open the same evidence panel everything else does.
 * - **derived** — a count or a min/max the system computed *from* the citation set. The
 *   number was never taken from the model. Expanding shows the full support set, so
 *   "12" can be checked by counting twelve quotes.
 * - **gap** — searched, nothing found, and it can show the candidates it rejected.
 *   Absence with working shown is far stronger than absence asserted.
 * - **human** — pinned by an investigator. Marked as such forever; a human note must
 *   never be mistakable for a model claim.
 */

import { useEffect, useState } from "react";
import { api, type ArtifactVersion, type Unit } from "../lib/api";
import { useEvidence } from "../state/evidence";

interface Props {
  datasetId: string;
  artifactId: string;
  versionId: string;
}

export function ArtifactTable({ datasetId, artifactId, versionId }: Props) {
  const [version, setVersion] = useState<ArtifactVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .version(datasetId, artifactId, versionId)
      .then((fetched) => {
        if (!cancelled) setVersion(fetched);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [datasetId, artifactId, versionId]);

  if (error) {
    return (
      <main className="page">
        <p data-testid="artifact-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      </main>
    );
  }
  if (!version) return <main className="page">loading…</main>;

  const columns = version.layout?.columns ?? [];
  const rows = version.layout?.rows ?? [];

  return (
    <main className="page artifact">
      <header>
        <h1>{version.title}</h1>
        <p className="eyebrow" data-testid="artifact-status">
          v{version.version_no} · {version.status}
          {version.stale ? " · stale" : ""}
        </p>
        {version.status === "partial" && (
          <p className="partial" data-testid="artifact-partial">
            A budget cap stopped this run early. What is here is complete and cited; what
            is missing is missing, and the table does not pretend otherwise.
          </p>
        )}
        <p>
          <a
            href={api.exportUrl(datasetId, artifactId, versionId)}
            data-testid="export-link"
            download
          >
            Export bundle (.zip)
          </a>{" "}
          <span className="mono" style={{ color: "var(--ink-faint)" }}>
            includes an offline verifier that re-checks every quote and every count
          </span>
        </p>
      </header>

      <div className="table-scroll">
        <table className="grid" data-testid="artifact-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} data-testid="artifact-row">
                {columns.map((column) => {
                  const unitId = row.cells[column.key]?.u;
                  const unit = unitId ? version.units[unitId] : undefined;
                  return (
                    <td key={column.key}>
                      {unit ? <Cell unit={unit} /> : <span className="empty">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p data-testid="artifact-empty" style={{ color: "var(--ink-faint)" }}>
          This version has no rows.
        </p>
      )}
    </main>
  );
}

function Cell({ unit }: { unit: Unit }) {
  const { select, selected } = useEvidence();
  const [expanded, setExpanded] = useState(false);
  const isDerived = unit.kind === "derived";
  const isGap = unit.kind === "gap";
  const isHuman = unit.origin === "human";

  // `text` is the rendered form for every unit kind, including derived — the server
  // writes it from the value it computed. `value.v` is the fallback, not the source:
  // reading the typed object first would print "[object Object]" the moment the shape
  // gained a field.
  const display = unit.text ?? unit.value?.v ?? "";

  return (
    <div className={`cell ${unit.kind} ${unit.origin}`} data-kind={unit.kind} data-origin={unit.origin}>
      {isGap ? (
        <span className="gap-mark" data-testid="cell-gap">
          not found in this corpus
        </span>
      ) : (
        <span data-testid="cell-value">{display}</span>
      )}

      {isHuman && (
        <span className="human-mark" data-testid="cell-human" title="Pinned by an investigator">
          pinned
        </span>
      )}

      {isDerived && (
        <button
          className="derived-mark"
          data-testid="cell-derived"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          title="Computed from the citations below, not taken from the model"
        >
          {String(unit.computation?.operator ?? "derived")} of {unit.citations.length}
        </button>
      )}

      {(!isDerived || expanded) && (
        <span className="cites" data-testid="cell-cites">
          {unit.citations.map((citation, index) => {
            const isSelected =
              selected?.blockId === citation.block_id &&
              selected.spans[0]?.start_cp === citation.start_cp;
            return (
              <button
                key={citation.span_id}
                className="cite"
                data-testid="cite"
                aria-current={isSelected}
                title={citation.quote}
                onClick={() =>
                  select({
                    blockId: citation.block_id,
                    spans: [
                      {
                        start_cp: citation.start_cp,
                        end_cp: citation.end_cp,
                        quote: citation.quote,
                      },
                    ],
                    label: `E${index + 1}`,
                    origin: "table",
                  })
                }
              >
                E{index + 1}
              </button>
            );
          })}
        </span>
      )}

      {isGap && unit.rejected_candidates.length > 0 && (
        <details className="rejected" data-testid="cell-rejected">
          <summary>
            {unit.rejected_candidates.length} candidate
            {unit.rejected_candidates.length === 1 ? "" : "s"} considered and rejected
          </summary>
          <ul>
            {unit.rejected_candidates.map((candidate) => (
              <li key={candidate.span_id} className="mono">
                {candidate.quote}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
